/**
 * Reaction Engine
 *
 * Perception-based defender reactions, using a "simulate-then-animate" model:
 *   1. Sample an offensive player's route into an observation feed (positions over time).
 *   2. Run a defender read policy tick-by-tick against that feed, integrating the
 *      defender's movement with a capped closing speed.
 *   3. Return the defender's resulting trajectory, which the AnimationEngine plays back.
 *
 * The defender logic is given ONLY the observation feed (where the key is over time),
 * never the route name. Decisions are derived from observable kinematics — depth and
 * velocity/heading — so the same policy works for any route, including ones a coach
 * adds later.
 *
 * Pure module: no React, no side effects. Safe to unit test.
 */

import { Position, Waypoint, Observation, ManMatchParams, UnderneathMatchParams, HookReadParams, ApexMatchParams, DeepZoneParams, ManTrailParams, PalmsCornerParams } from '../types';
import {
  DEFAULT_KINEMATICS,
  createMotionState,
  inferTechnique,
  stepDefender,
} from './kinematics';
import { executeResponsibility, ResponsibilityResult } from './responsibility';
import { MAN_MATCH_SPEC } from './specs/manMatchSpec';
import { UNDERNEATH_MATCH_SPEC } from './specs/underneathMatchSpec';
import { MAN_TRAIL_SPEC } from './specs/manTrailSpec';
import { PALMS_CORNER_SPEC, PALMS_SAFETY_SPEC } from './specs/palmsSpecs';
import { APEX_MATCH_SPEC } from './specs/apexMatchSpec';
import { HOOK_READ_SPEC } from './specs/hookReadSpec';

/** Fixed simulation timestep (ms). ~16 samples/sec — dense enough for smooth keyframe playback. */
export const SIM_DT_MS = 60;

/**
 * Minimum simulation horizon (ms). Short routes (e.g. a 700ms hitch) still get a
 * full play window so the defender's zone drop / settle is visible.
 */
export const MIN_SIM_HORIZON_MS = 2600;

/**
 * Default parameters for the man-match read.
 * Calibrated to this app's coordinate system: LOS ~y=85, 1 yard ≈ 3 y-units,
 * WR cruising speed ≈ 28-30 units/sec.
 */
export const DEFAULT_MAN_MATCH_PARAMS: ManMatchParams = {
  readDepthYards: 10,        // measured off the key's own alignment, so #1 and #2 share it
  breakDepthToleranceY: 4,   // breaks within ~1.3yd of the landmark still count as vertical
  breakAngleDeg: 40,         // heading change that counts as a hard break
  settleSpeedFraction: 0.4,  // dropping below 40% of peak speed = a stop/settle
  maxSpeed: 33,              // slightly faster than the WR so man coverage can stay in phase
  readSpeed: 33,             // corners read at full pace (backpedal keeps them smooth); safeties override slower
  zoneDropSpeed: 20,         // controlled backpedal pace for the zone drop (not a sprint)
  readCushion: 14,           // ~4.5yd cushion maintained while reading
  trailCushion: 5,           // trail leverage once locked in man
  overTheTop: false,         // corners play tight trail (can drive down on stops)
  readTowardZone: false,     // corners mirror the key + backpedal during the read
  zoneLandmark: { x: 18, y: 30 }, // top of the deep third on the LEFT side (CB1) — he keeps dropping to here
};

/**
 * Right-side (CB2) man-match params. Identical to the left default except the defender
 * bails to the deep RIGHT third. The read itself is side-agnostic (depth + heading-from-stem
 * are symmetric), so only the zone landmark mirrors across midfield.
 */
export const DEFAULT_MAN_MATCH_PARAMS_RIGHT: ManMatchParams = {
  ...DEFAULT_MAN_MATCH_PARAMS,
  zoneLandmark: { x: 82, y: 30 }, // mirror of the left landmark (100 - 18) — deep right third
};

/**
 * Free Safety man-match params (keys WR2/#2, left side).
 *
 * Reads the same ten-yard rule as the corners — `readDepthYards` is resolved off WR2's own
 * alignment, so no offset is applied by hand. On a short break the FS maintains his drop into a
 * deep inside-left quarter.
 */
export const DEFAULT_MAN_MATCH_PARAMS_FS: ManMatchParams = {
  ...DEFAULT_MAN_MATCH_PARAMS,
  readSpeed: 14,        // patient, slow-motion read from depth before committing
  overTheTop: true,     // carry the vertical over the top — no upfield lurch at commit
  readTowardZone: true, // drop straight back instead of chasing WR2 laterally
  // Straight backpedal from the FS alignment {35,45}, same x. y is a FLOOR, not a target —
  // deep enough that a typical play ends before he ever reaches it (see readTowardZone).
  zoneLandmark: { x: 35, y: 5 },
};

/**
 * Strong Safety man-match params (keys WR3/#2 strong, right side).
 * Same ten-yard rule as the FS, resolved off WR3's own alignment; drops to the deep
 * inside-right quarter on a short break.
 */
export const DEFAULT_MAN_MATCH_PARAMS_SS: ManMatchParams = {
  ...DEFAULT_MAN_MATCH_PARAMS,
  readSpeed: 14,        // patient, slow-motion read from depth before committing
  overTheTop: true,     // carry the vertical over the top — no upfield lurch at commit
  readTowardZone: true, // drop straight back instead of chasing WR3 laterally
  // Straight backpedal from the SS alignment {65,45}, same x. y is a FLOOR, not a target —
  // deep enough that a typical play ends before he ever reaches it (see readTowardZone).
  zoneLandmark: { x: 65, y: 5 },
};

/**
 * Linearly interpolate a route's position at time `t` (ms).
 * Before the first waypoint returns the start; after the last returns the final
 * position (the player holds where his route ended).
 */
export function samplePositionAtTime(waypoints: Waypoint[], t: number): Position {
  if (waypoints.length === 0) return { x: 0, y: 0 };
  if (waypoints.length === 1 || t <= waypoints[0].timing) {
    return { ...waypoints[0].position };
  }
  const last = waypoints[waypoints.length - 1];
  if (t >= last.timing) return { ...last.position };

  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    if (t <= b.timing) {
      const span = b.timing - a.timing;
      const frac = span > 0 ? (t - a.timing) / span : 0;
      return {
        x: a.position.x + (b.position.x - a.position.x) * frac,
        y: a.position.y + (b.position.y - a.position.y) * frac,
      };
    }
  }
  return { ...last.position };
}

/**
 * Build a uniform time-sampled observation feed of a route over [0, horizonMs].
 * This is the "what the defender sees" input — positions only, no route identity.
 */
export function buildObservationFeed(
  waypoints: Waypoint[],
  horizonMs: number,
  dtMs: number = SIM_DT_MS
): Observation[] {
  const feed: Observation[] = [];
  for (let t = 0; t <= horizonMs; t += dtMs) {
    feed.push({ t, position: samplePositionAtTime(waypoints, t) });
  }
  // Guarantee the final horizon sample is present even if horizon isn't a multiple of dt.
  const lastT = feed.length > 0 ? feed[feed.length - 1].t : -1;
  if (lastT < horizonMs) {
    feed.push({ t: horizonMs, position: samplePositionAtTime(waypoints, horizonMs) });
  }
  return feed;
}

/**
 * Mirror a route's waypoints across midfield (x -> 100 - x), leaving depth (y) unchanged.
 * Routes are authored for the left side (WR1); right-side receivers (WR4) run the same
 * shape mirrored. Use this so a right-side defender's observation feed matches the
 * receiver's real field positions.
 */
export function mirrorRouteX(waypoints: Waypoint[]): Waypoint[] {
  return waypoints.map((wp) => ({
    ...wp,
    position: { x: 100 - wp.position.x, y: wp.position.y },
  }));
}

// ReadState is owned by ./responsibility (the declarative layer) and re-exported here so
// existing importers of the engine keep working unchanged.
export type { ReadState } from './responsibility';




/**
 * Full result of a read — the trajectory, the observable decision it made, and the tick-by-tick
 * trace of the reasoning behind it.
 *
 * Every read policy is now a spec executed by the shared interpreter, so this is simply
 * {@link ResponsibilityResult} under its historical name. Kept as an alias because the name
 * reads well at call sites and appears throughout the specs.
 */
export type ManMatchResult = ResponsibilityResult;

/**
 * Resolve a man-match ("MOD") defender trajectory against a key's observation feed.
 *
 * Thin wrapper over {@link resolveManMatchDetailed} that returns only the trajectory,
 * which is what the animation layer consumes.
 *
 * @returns One defender Position per feed sample (same length as `keyFeed`), starting at `defenderStart`.
 */
export function resolveManMatch(
  defenderStart: Position,
  keyFeed: Observation[],
  params: ManMatchParams = DEFAULT_MAN_MATCH_PARAMS
): Position[] {
  return resolveManMatchDetailed(defenderStart, keyFeed, params).trajectory;
}

/**
 * Resolve a man-match ("MOD") read and return the full result (trajectory + decision).
 *
 * The read, entirely in observable terms:
 *  - While READing, mirror the key laterally and backpedal to hold a cushion.
 *  - If the key makes a hard break (sharp heading change OR a stop) while still shallow
 *    (above the read landmark) → SETTLE to the zone landmark (pass him off).
 *  - If the key crosses the read landmark still on his vertical stem → commit to MAN
 *    and trail him the rest of the way.
 */
export function resolveManMatchDetailed(
  defenderStart: Position,
  keyFeed: Observation[],
  params: ManMatchParams = DEFAULT_MAN_MATCH_PARAMS
): ManMatchResult {
  // Now a thin adapter over the declarative spec (src/rules/specs/manMatchSpec.ts). The read
  // itself — latches, transitions, per-state behavior — lives there as data; the shared
  // interpreter supplies perception, latency, movement and decision bookkeeping.
  return executeResponsibility(
    MAN_MATCH_SPEC,
    defenderStart,
    keyFeed,
    params,
    params.kinematics ?? DEFAULT_KINEMATICS
  );
}

/**
 * Default underneath-match params for DB/LB1 (left-side apex/slot, keys WR2/#2).
 *
 * The vertical landmark is resolved off WR2's own alignment, so "reached 10 yards vertical"
 * needs no hand-applied offset. `outSignX` is -1 because a left-side #2's
 * out route breaks toward the left sideline (decreasing x). On a pass-off the defender settles
 * into the left curl-flat.
 */
export const DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1: UnderneathMatchParams = {
  verticalDepthYards: 10,    // off #2's own alignment — no hand-applied offset needed
  breakDepthToleranceY: 4,   // vertical line = 64
  breakAngleDeg: 40,         // heading change that counts as a hard break
  settleSpeedFraction: 0.4,  // dropping below 40% of peak speed = a stop/hitch
  backToLosSpeed: 3,         // coming back toward the QB (y increasing) faster than this = a hitch
  outSignX: -1,              // left side: an out route breaks toward decreasing x
  lateralEps: 4,             // min lateral speed for a break to count as directional
  maxSpeed: 33,              // closing/trail speed once he jumps the underneath route
  readSpeed: 16,             // patient shuffle mirroring #2 before committing
  zoneDropSpeed: 18,         // controlled drop into the curl-flat after a pass-off
  trailCushion: 2,           // sit just underneath the receiver in man
  zoneLandmark: { x: 15, y: 64 }, // left curl-to-flat settle spot
  // "Push" receive: the back releasing fast/wide to the LEFT (decreasing x) flat, still shallow,
  // makes the apex come off #2 and man him. Inert unless an RB feed is supplied (Cover 6 doesn't).
  rbStartX: 55,
  rbFlatSignX: -1,           // left flat: the back widens toward decreasing x
  rbFlatThresholdX: 18,      // past ~18 units from his start = fast to the flat (matches the hook)
  rbFlatDepthY: 68,          // ...while still shallower than the hook (flat routes sit ~y77-85)
  // "#1 short in-breaker": WR1 (left #1) cutting shallow toward the middle, with no one in the
  // flat, pulls the apex off #2 to cover #1 across the field. Inert unless a #1 feed is supplied.
  key1OutSignX: -1,          // left #1 breaks OUT toward decreasing x; an in-breaker is +x
  key1DeepLineYards: 12.33,  // #1 deeper than this on his break = a Dig/vertical, the safety's
  key1ShortLineYards: 7.67,  // #1 shallower than this when he cuts in = a short in-breaker to rob
};

/**
 * Default underneath-match params for DB/LB2 (right-side apex/slot, keys WR3/#2 strong).
 *
 * Mirror of {@link DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1} across midfield: `outSignX` is +1
 * because a right-side #2's out route breaks toward the right sideline (increasing x), and
 * the zone landmark mirrors to the right curl-flat. The vertical landmark needs no change —
 * it resolves off WR3's own alignment.
 */
export const DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2: UnderneathMatchParams = {
  ...DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1,
  outSignX: 1,                     // right side: an out route breaks toward increasing x
  zoneLandmark: { x: 85, y: 64 },  // right curl-to-flat settle spot (mirror of x:15)
  rbFlatSignX: 1,                  // right flat: the back widens toward increasing x (mirror)
  key1OutSignX: 1,                 // right #1 (WR4) breaks OUT toward increasing x; in-breaker is -x
};

/**
 * Resolve an underneath-match ("Wall-Read on #2") defender trajectory against a key's feed.
 *
 * Thin wrapper over {@link resolveUnderneathMatchDetailed} returning only the trajectory,
 * which is what the animation layer consumes.
 */
export function resolveUnderneathMatch(
  defenderStart: Position,
  keyFeed: Observation[],
  params: UnderneathMatchParams = DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1,
  rbFeed?: Observation[],
  key1Feed?: Observation[]
): Position[] {
  return resolveUnderneathMatchDetailed(defenderStart, keyFeed, params, rbFeed, key1Feed).trajectory;
}

/**
 * Resolve an underneath-match ("Wall-Read on #2") read and return the full result.
 *
 * The read, entirely in observable terms (the defender is given only the key's positions
 * over time, never the route name):
 *  - While READing, mirror the key laterally and hold underneath depth.
 *  - If the key makes a SHALLOW break (above the vertical line):
 *      * coming back toward the LOS (a hitch/settle) OR stopped  → MAN (jump it).
 *      * a lateral break toward the sideline (out)               → MAN (jump it).
 *      * a lateral break toward the middle (short in-breaker)    → ZONE (pass off, drop to flat).
 *  - If the key reaches the vertical line still on his stem (vertical threat) → ZONE
 *    (wall him, then settle into the curl-flat while the safety/corner carry the vertical).
 *  - If #1 works SHORT INSIDE and nobody is in the flat (requires the `key1*` params AND a #1
 *    feed), come off #2 and MAN #1 across the field ("reroute #2, cover #1"). A loose back on a
 *    Push outranks this.
 *
 * Reuses {@link ManMatchResult} for symmetry with the corner/safety reads — `finalState`
 * is 'MAN' (jumped the underneath route / took the back / came off onto #1), 'ZONE' (passed
 * off to the flat/curl), or 'READ' (never resolved).
 *
 * @param rbFeed   the back's feed, for the "Push" read (slot RB=1). Optional.
 * @param key1Feed the #1 receiver's feed, for the "#1 short in-breaker" read (slot KEY1=0). Optional.
 */
export function resolveUnderneathMatchDetailed(
  defenderStart: Position,
  keyFeed: Observation[],
  params: UnderneathMatchParams = DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1,
  rbFeed?: Observation[],
  key1Feed?: Observation[]
): ManMatchResult {
  // Thin adapter over the declarative spec (src/rules/specs/underneathMatchSpec.ts). Partner feeds
  // go in the slot order the spec expects: #1 first (KEY1=0), the back second (RB=1). An empty
  // feed is harmless — its predicate is gated on `!!obs` and the matching params — so a variant
  // that supplies neither (e.g. a Cover 6 apex) keeps the plain wall-read.
  //   - With a #1 feed AND key1* params, the apex comes off #2 to cover a #1 short in-breaker.
  //   - With an RB feed AND rb* params, the apex takes the back on a "Push".
  return executeResponsibility(
    UNDERNEATH_MATCH_SPEC,
    defenderStart,
    keyFeed,
    params,
    params.kinematics ?? DEFAULT_KINEMATICS,
    [key1Feed ?? [], rbFeed ?? []]
  );
}

/**
 * Default "Play RB" hook-read params for the LB (middle, keys the RB / #3) in Cover 4 Match.
 *
 * The RB aligns at ~x=55 in the backfield; `flatThresholdX` of 18 means a release that widens
 * past ~x=73 (or ~x=37) while still shallow is a fast-flat/out release the LB does NOT chase.
 * A central release that settles (a checkdown-type route) is taken man-to-man. When the RB does
 * neither (stays in) the LB simply keeps his slow hook drop. `hookLandmark` is the middle hook.
 */
export const DEFAULT_HOOK_READ_PARAMS_LB: HookReadParams = {
  flatThresholdX: 18,        // widen past ~18 units from his start = fast to the flat/out
  flatDepthY: 68,            // ...while still shallower than the hook (RB flat routes stay ~y77-85)
  releaseThresholdDist: 8,   // moved at least this far from his start = he released
  settleSpeedFraction: 0.5,  // dropping below half of peak speed = settling (a checkdown)
  backToLosSpeed: 3,         // coming back toward the QB faster than this = settling
  maxSpeed: 30,              // trail speed once he mans up on the RB (or a crosser)
  readSpeed: 10,             // patient slow drop into the hook while reading
  zoneDropSpeed: 14,         // hold/settle pace into the hook once he passes the RB off
  trailCushion: 2,           // sit just underneath the RB/crosser in man
  hookLandmark: { x: 50, y: 58 }, // middle hook (~9 yards), eyes for crossers
  crosserBandMinY: 62,       // don't chase a crosser deeper than ~7.5 yards (over his head)
  crosserBandMaxY: 82,       // ignore routes flat at the LOS
  crosserLateralSpeed: 14,   // min lateral speed to count as crossing (vs a settle/vertical)
  crosserZoneHalfWidth: 20,  // match a crosser once he's within ~20 units of the middle
  crosserMiddleX: 50,        // field center — a crosser must be working toward it
  // ownSideSign omitted: the single middle LB mans any checkdown RB (no side split).
};

/**
 * Resolve the LB's "Play RB" hook-read trajectory. Thin wrapper over
 * {@link resolveHookReadDetailed} returning only the trajectory (what the animation consumes).
 *
 * `crosserFeeds` are the observation feeds of the other eligible receivers (the WRs), used only
 * when there is no RB to man. Omit them to get the pure RB read (holds the hook when no RB).
 */
export function resolveHookRead(
  defenderStart: Position,
  keyFeed: Observation[],
  params: HookReadParams = DEFAULT_HOOK_READ_PARAMS_LB,
  crosserFeeds: Observation[][] = [],
  key2Feed?: Observation[]
): Position[] {
  return resolveHookReadDetailed(defenderStart, keyFeed, params, crosserFeeds, key2Feed).trajectory;
}


/**
 * Resolve the LB's "Play RB" read and return the full result.
 *
 * The read, entirely in observable terms (the LB is given only positions over time):
 *  - **RB is the primary.** If the RB releases centrally and SETTLES (a checkdown) → MAN the RB.
 *  - If the RB is FAST TO THE FLAT / runs an out, or STAYS IN, there is no RB to cover, so:
 *      * scan the `crosserFeeds` and MAN the FIRST receiver that crosses into his hook area — a
 *        receiver moving laterally toward the middle, at a matchable underneath depth, within
 *        `crosserZoneHalfWidth` of `hookLandmark.x`.
 *      * if no crosser appears, hold / slow-drop the hook (ZONE).
 *
 * Priority between the RB and a crosser lives in the spec's binding: a crosser is only hunted
 * when the RB is not this defender's to man, unless `crossersBeforeRb` inverts that.
 *
 * Reuses {@link ManMatchResult} — `finalState` is 'MAN' (matched the RB OR a crosser), 'ZONE'
 * (held the hook, no one to match), or 'READ' (never committed).
 */
export function resolveHookReadDetailed(
  defenderStart: Position,
  keyFeed: Observation[],
  params: HookReadParams = DEFAULT_HOOK_READ_PARAMS_LB,
  crosserFeeds: Observation[][] = [],
  key2Feed?: Observation[]
): ManMatchResult {
  // Thin adapter over the declarative spec (src/rules/specs/hookReadSpec.ts). The RB is the
  // primary key; the crosser candidates are partner feeds he may bind to.
  //
  // When #2 is supplied he is appended as one more partner and his slot recorded in params, which
  // is what enables the "Push" call — the hook taking #2 when the back gets away from him. Omit
  // him and the read is unchanged, so call sites without a Push rule need no update.
  const partners = key2Feed ? [...crosserFeeds, key2Feed] : crosserFeeds;
  const effective = key2Feed ? { ...params, pushKey2Slot: crosserFeeds.length } : params;
  return executeResponsibility(
    HOOK_READ_SPEC,
    defenderStart,
    keyFeed,
    effective,
    effective.kinematics ?? DEFAULT_KINEMATICS,
    partners
  );
}

// ============================================================================
// Cover 3 Match reads
// ============================================================================

/**
 * Cover 3 Match corner params. Same MOD read as the Cover 4 corners (man the #1 if he gets
 * vertical past ~10 yards, otherwise bail), but the corner drops to his deep OUTSIDE THIRD
 * rather than a quarter — so only the zone landmark differs.
 */
export const DEFAULT_MAN_MATCH_PARAMS_CB1_COVER3: ManMatchParams = {
  ...DEFAULT_MAN_MATCH_PARAMS,
  zoneLandmark: { x: 14, y: 22 }, // deep left third
};
export const DEFAULT_MAN_MATCH_PARAMS_CB2_COVER3: ManMatchParams = {
  ...DEFAULT_MAN_MATCH_PARAMS_RIGHT,
  zoneLandmark: { x: 86, y: 22 }, // deep right third
};

/**
 * Cover 3 Match hook params for the two inside hook defenders (LB left, DB/LB2 right). Both run
 * the "Play RB + crossers" hook read, but each owns only his half: `ownSideSign` gates which hook
 * takes a checkdown RB (so they don't double-cover him), and each matches crossers into his own
 * hook (`hookLandmark`) while judging "toward the middle" against field center (`crosserMiddleX`).
 */
export const DEFAULT_HOOK_READ_PARAMS_LB_COVER3: HookReadParams = {
  ...DEFAULT_HOOK_READ_PARAMS_LB,
  hookLandmark: { x: 42, y: 58 }, // left inside hook
  crosserZoneHalfWidth: 20,
  crosserMiddleX: 50,
  ownSideSign: -1, // only mans a checkdown RB that settles left of center
  pushReachX: 25, // can get to his own side's #2, not the far one
};
export const DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER3: HookReadParams = {
  ...DEFAULT_HOOK_READ_PARAMS_LB,
  hookLandmark: { x: 58, y: 58 }, // right inside hook
  crosserZoneHalfWidth: 20,
  crosserMiddleX: 50,
  ownSideSign: 1, // only mans a checkdown RB that settles right of center
  pushReachX: 25, // can get to his own side's #2, not the far one
};

/** Deep-middle (single-high) zone params for the Cover 3 Match FS. */
export const DEFAULT_DEEP_ZONE_PARAMS_FS: DeepZoneParams = {
  laneX: 50, // midfield
  floorY: 12, // deep middle third
  dropSpeed: 18,
};

/**
 * Resolve a pure deep-zone drop (Cover 3 Match FS / single-high middle). No key — the defender
 * backpedals toward his deep landmark and holds, reading the whole field. Returns one Position
 * per simulation tick over the horizon (same shape the trajectory animation consumes).
 */
export function resolveDeepZone(
  defenderStart: Position,
  horizonMs: number,
  params: DeepZoneParams = DEFAULT_DEEP_ZONE_PARAMS_FS
): Position[] {
  const trajectory: Position[] = [{ ...defenderStart }];
  const kin = params.kinematics ?? DEFAULT_KINEMATICS;
  let motion = createMotionState(defenderStart, 'backpedal');
  let defender: Position = motion.position;
  const dtSec = SIM_DT_MS / 1000;
  const steps = Math.max(1, Math.round(horizonMs / SIM_DT_MS));
  const target: Position = { x: params.laneX, y: params.floorY };
  for (let i = 0; i < steps; i++) {
    motion = stepDefender(motion, target, params.dropSpeed, inferTechnique(defender, target, params.dropSpeed, kin), dtSec, kin);
    defender = motion.position;
    trajectory.push({ ...defender });
  }
  return trajectory;
}

/** Default apex ("match #2") params for DB/LB1 (left, keys WR2; secondary WR1; RB to left flat). */
export const DEFAULT_APEX_MATCH_PARAMS_DBLB1: ApexMatchParams = {
  verticalDepthYards: 10, // off #2's own alignment
  key1DeepLineYards: 12.33, // #1 this deep on his stem = gone (not a short/curl)
  key1ShortLineYards: 7.67, // #1's short-out pivot / curl mark
  breakDepthToleranceY: 4, // #2 vertical line = 64
  breakAngleDeg: 40,
  settleSpeedFraction: 0.4,
  backToLosSpeed: 3,
  insideSignX: 1, // left #2 (WR2) breaks inside toward the middle (increasing x)
  key1OutSignX: -1, // left #1 (WR1) breaks out toward the left sideline (decreasing x)
  // A real slant/drag cuts across fast (|vx| ~ 23-31); a hitch's settle drifts only ~10. This
  // threshold keeps a hitch (a short route the apex keeps manning) from reading as an inside cut.
  lateralEps: 14,
  rbStartX: 55,
  rbFlatSignX: -1, // this apex's flat is to the LEFT (RB widening to decreasing x)
  rbFlatThresholdX: 18,
  rbFlatDepthY: 68,
  maxSpeed: 33,
  readSpeed: 30,
  zoneDropSpeed: 18,
  trailCushion: 3,
  zoneLandmark: { x: 35, y: 55 }, // left curl-hook settle when #2 is passed off
};

/** Default apex ("match #2") params for SS (right, keys WR3; secondary WR4; RB to right flat). */
export const DEFAULT_APEX_MATCH_PARAMS_SS: ApexMatchParams = {
  ...DEFAULT_APEX_MATCH_PARAMS_DBLB1,
  insideSignX: -1, // right #2 (WR3) breaks inside toward the middle (decreasing x)
  key1OutSignX: 1, // right #1 (WR4) breaks out toward the right sideline (increasing x)
  rbFlatSignX: 1, // this apex's flat is to the RIGHT (RB widening to increasing x)
  zoneLandmark: { x: 65, y: 55 }, // right curl-hook settle
};




/**
 * Resolve the Cover 3 Match apex ("match #2") trajectory. Thin wrapper over
 * {@link resolveApexMatchDetailed} returning only the trajectory.
 */
export function resolveApexMatch(
  defenderStart: Position,
  key2Feed: Observation[],
  params: ApexMatchParams = DEFAULT_APEX_MATCH_PARAMS_DBLB1,
  key1Feed: Observation[] = [],
  rbFeed: Observation[] = [],
  switchFeed: Observation[] = []
): Position[] {
  return resolveApexMatchDetailed(defenderStart, key2Feed, params, key1Feed, rbFeed, switchFeed).trajectory;
}

/**
 * Resolve the Cover 3 Match apex read and return the full result.
 *
 * The apex mans #2 by default. Observing #2, #1 and the RB (never route names), he:
 *  - carries #2 man-to-man if #2 gets VERTICAL — the corner/FS help deep, so a late RB flare
 *    does NOT pull him off a vertical #2;
 *  - comes off to rob #1 if #1 runs a SHORT OUT, a SHORT IN-BREAKER, or a CURL (the corner
 *    passes #1 off to him); the deep dig/post climb past his short line and stay the safety's;
 *  - if #2 breaks SHORT INSIDE (a crosser for the hooks), passes #2 off and — free now — takes the
 *    RB if it flared to his flat, else robs a short #1, else settles into the curl-hook (ZONE);
 *  - otherwise (a short-out/hitch #2) keeps manning #2, unless a #1 short or RB flat pulls him off.
 *
 * `finalState`: 'MAN' (manned #2, #1, or the RB) or 'ZONE' (passed #2 off and settled).
 */
export function resolveApexMatchDetailed(
  defenderStart: Position,
  key2Feed: Observation[],
  params: ApexMatchParams = DEFAULT_APEX_MATCH_PARAMS_DBLB1,
  key1Feed: Observation[] = [],
  rbFeed: Observation[] = [],
  switchFeed: Observation[] = []
): ManMatchResult {
  // Thin adapter over the declarative spec (src/rules/specs/apexMatchSpec.ts). Partner feeds go
  // in the slot order the spec expects: #1, the back, then the switch receiver.
  return executeResponsibility(
    APEX_MATCH_SPEC,
    defenderStart,
    key2Feed,
    params,
    params.kinematics ?? DEFAULT_KINEMATICS,
    [key1Feed, rbFeed, switchFeed]
  );
}

// ============================================================================
// Cover 3 Match — 3x1 (trips) reads/params
// ============================================================================

/** Straight man-trail params (Cover 3 trips weak corner on the single WR4). */
export const DEFAULT_MAN_TRAIL_PARAMS: ManTrailParams = {
  maxSpeed: 33,
  trailCushion: 5,
};

/**
 * Resolve a pure man-trail trajectory — the defender trails his key everywhere, never bailing to
 * a zone (Cover 3 trips weak corner: "straight man-to-man on the single WR, no exceptions").
 */
export function resolveManTrailDetailed(
  defenderStart: Position,
  keyFeed: Observation[],
  params: ManTrailParams = DEFAULT_MAN_TRAIL_PARAMS
): ManMatchResult {
  // Thin adapter over the declarative spec (src/rules/specs/manTrailSpec.ts). Running through
  // the interpreter is what gives this defender the same reaction latency as everyone else.
  return executeResponsibility(
    MAN_TRAIL_SPEC,
    defenderStart,
    keyFeed,
    params,
    params.kinematics ?? DEFAULT_KINEMATICS
  );
}

/** Trajectory-only form, for callers that do not need the decision trace. */
export function resolveManTrail(
  defenderStart: Position,
  keyFeed: Observation[],
  params: ManTrailParams = DEFAULT_MAN_TRAIL_PARAMS
): Position[] {
  return resolveManTrailDetailed(defenderStart, keyFeed, params).trajectory;
}

/**
 * Trips (3x1) apex params for DB/LB1 — same left-side apex "match #2" (WR2) as doubles, but the
 * fast-to-flat threat is the strong-side #3 (WR3, aligned ~x40) rather than the RB. So the
 * flat-threat feed passed to {@link resolveApexMatch} is WR3, and the start/threshold are tuned
 * to WR3 breaking to the strong (left) flat.
 */
export const DEFAULT_APEX_MATCH_PARAMS_DBLB1_TRIPS: ApexMatchParams = {
  ...DEFAULT_APEX_MATCH_PARAMS_DBLB1,
  rbStartX: 40, // WR3's trips alignment (the flat threat here)
  rbFlatSignX: -1, // WR3 breaking to the strong (left) flat
  rbFlatThresholdX: 12,
};

/**
 * Trips (3x1) apex params for the LB — mans the strong-side #3 (WR3, aligned ~x40, inside = +x).
 * Passed a switch feed (WR2): on a WR3 SHORT OUT the LB rats to WR2; on a WR3 in-breaker he passes
 * off (ZONE) to the other hook (DB/LB2); otherwise he mans WR3.
 */
export const DEFAULT_APEX_MATCH_PARAMS_LB_TRIPS: ApexMatchParams = {
  ...DEFAULT_APEX_MATCH_PARAMS_DBLB1, // left-oriented: insideSignX +1 (WR3 inside = +x)
  zoneLandmark: { x: 48, y: 58 }, // hook settle when WR3 is passed off
};

/**
 * Trips (3x1) hook params for DB/LB2 — the middle hook. Matches the WR3 crosser (the LB's pass-off)
 * with PRIORITY over the RB (crossersBeforeRb); if there's no WR3 in-breaker it covers the RB only
 * on a middle (central checkdown) release — no ownSideSign, so any central checkdown counts.
 */
export const DEFAULT_HOOK_READ_PARAMS_DBLB2_TRIPS: HookReadParams = {
  ...DEFAULT_HOOK_READ_PARAMS_LB,
  hookLandmark: { x: 52, y: 58 },
  crosserZoneHalfWidth: 24,
  crosserMiddleX: 50,
  crossersBeforeRb: true, // a WR3 in-breaker takes priority over the RB
};

/**
 * Trips (3x1) params for SS — the WEAK apex. Mans the RB on ANY release to the weak (right) side
 * (fast flat included, via manAnyReleaseToSide), otherwise drops and matches crossers coming from
 * the 3-WR (strong) side.
 */
export const DEFAULT_HOOK_READ_PARAMS_SS_TRIPS: HookReadParams = {
  ...DEFAULT_HOOK_READ_PARAMS_LB,
  hookLandmark: { x: 64, y: 58 },
  crosserZoneHalfWidth: 24,
  crosserMiddleX: 50,
  ownSideSign: 1, // weak (right) side
  manAnyReleaseToSide: true, // takes the RB on any release to his side, not just a checkdown
};

// ============================================================================
// Cover 6 params (Cover 4 on the LEFT, Cover 2 on the RIGHT)
// ============================================================================
//
// Cover 6 reuses the existing reads:
//  - Cover 4 side (left): CB1 = resolveManMatch(DEFAULT_MAN_MATCH_PARAMS) MOD on WR1;
//    DB/LB1 = resolveUnderneathMatch(DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1) wall #2; FS =
//    resolveManMatch(DEFAULT_MAN_MATCH_PARAMS_FS) robber on WR2.
//  - Cover 2 side (right): CB2 = resolveManTrail (match #1 = WR4 everywhere); DB/LB2 =
//    resolveUnderneathMatch(DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2) match #2 = WR3; SS = deep half.
//  - Middle: LB = resolveHookRead(DEFAULT_HOOK_READ_PARAMS_LB) cross/#3.
// Only the two reads below need Cover-6-specific tuning.

/**
 * Cover 6 SS — the deep HALF safety on the Cover 2 (right) side. A pure deep-zone drop like the
 * Cover 3 FS, but sitting in the middle of the right half rather than the deep middle third.
 */
export const DEFAULT_DEEP_ZONE_PARAMS_SS_COVER6: DeepZoneParams = {
  laneX: 72, // middle of the right half
  floorY: 15, // deep
  dropSpeed: 18,
};

/**
 * Cover 6 trips DB/LB2 — the single (weak/right) side underneath defender. He has no RB
 * responsibility (the LB hammers #3), so `ownSideSign: -1` makes the right-releasing RB never
 * "his" → he is a pure crosser matcher, picking up the first route crossing from the 3-WR side.
 */
export const DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER6: HookReadParams = {
  ...DEFAULT_HOOK_READ_PARAMS_LB,
  hookLandmark: { x: 70, y: 58 }, // single-side underneath spot
  crosserZoneHalfWidth: 26,
  crosserMiddleX: 50,
  ownSideSign: -1, // RB (releases right/central) is never on his side → crosser-only
};

// ============================================================================
// Cover 4 Palms reads/params
// ============================================================================
//
// Cover 4 Palms is Cover 4 Match with a "Palms" twist on the corners: the corner still plays a
// MOD man-match on #1, but jumps #2 man-to-man if #2 releases on an early short out (the "Out"
// call). Every other position reuses an existing read:
//  - DB/LB1 / DB/LB2 (apex, "Wall-Read on #2") → resolveUnderneathMatch (same as Cover 4 Match).
//  - FS / SS (safety, "Robber-Read") → resolveManMatch FS/SS (same robber read as Cover 4 Match /
//    Cover 6). NOTE: the "#2 out early → man #1" robber exception is simplified — the safety
//    reads #2 only (man vertical #2, else the deep quarter), matching the reused robber read.
//  - LB (Hammer on #3) → resolveHookRead (same "Play RB + crossers" read as Cover 4 Match).
// Trips (3-WR strong LEFT, single WR4 weak RIGHT) mirrors the strong side to Palms and simplifies
// the weak side: CB2 = resolveManTrail (man WR4), DB/LB2 = weak apex (reuses the Cover 3 trips
// SS weak-apex hook params), SS = poach → a deep-help zone (a simplification of the bracket rule).

/**
 * Cover 4 Palms apex params — DB/LB1 (left, "Wall-Read on #2" = WR2). Same underneath wall as
 * Cover 4 Match EXCEPT a #2 short out is passed off (ZONE), not manned: the Palms corner makes the
 * "Out" call and takes #2's out, so the apex sinks to the flat/curl and reads instead of jumping it.
 */
export const DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1_PALMS: UnderneathMatchParams = {
  ...DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1,
  passOffOutBreak: true,
};

/** Cover 4 Palms apex params — DB/LB2 (right, "Wall-Read on #2" = WR3). Mirror of the DB/LB1 Palms apex. */
export const DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2_PALMS: UnderneathMatchParams = {
  ...DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2,
  passOffOutBreak: true,
};

/** Cover 4 Palms corner params — CB1 (Palms on WR1; jumps a WR2 early short out to the LEFT flat). */
export const DEFAULT_PALMS_CORNER_PARAMS_CB1: PalmsCornerParams = {
  manMatch: DEFAULT_MAN_MATCH_PARAMS, // MOD on #1, bail to the deep left quarter
  out2SignX: -1,     // WR2 (left #2) out breaks toward the left sideline (decreasing x)
  out2DepthY: 68,    // WR2's short out breaks at offset y~75 (early); its deepOut breaks at y~50 (deep)
  out2LateralEps: 12,
  breakAngleDeg: 40,
  maxSpeed: 33,
  trailCushion: 3,
};

/** Cover 4 Palms corner params — CB2 (Palms on WR4; jumps a WR3 early short out to the RIGHT flat). */
export const DEFAULT_PALMS_CORNER_PARAMS_CB2: PalmsCornerParams = {
  ...DEFAULT_PALMS_CORNER_PARAMS_CB1,
  manMatch: DEFAULT_MAN_MATCH_PARAMS_RIGHT, // MOD on #1, bail to the deep right quarter
  out2SignX: 1,      // WR3 (right #2) out breaks toward the right sideline (increasing x)
};

/**
 * Cover 4 Palms trips SS — the WEAK (single-WR) side "poach" safety. Modeled as a deep-help zone
 * leaning to the middle/weak side (a simplification of the bracket-#3-vertical / bracket-weak-#1
 * rule): the true poach reads #3 and weak #1 and brackets one; here he plays a deep help lane.
 */
export const DEFAULT_DEEP_ZONE_PARAMS_SS_PALMS: DeepZoneParams = {
  laneX: 62,  // between the middle (#3 vertical help) and the weak WR4
  floorY: 15, // deep
  dropSpeed: 18,
};


/**
 * Resolve the Cover 4 Palms corner ("Play Palms on #1") trajectory. Thin wrapper over
 * {@link resolvePalmsCornerDetailed} returning only the trajectory (what the animation consumes).
 */
export function resolvePalmsCorner(
  defenderStart: Position,
  key1Feed: Observation[],
  key2Feed: Observation[],
  params: PalmsCornerParams = DEFAULT_PALMS_CORNER_PARAMS_CB1
): Position[] {
  return resolvePalmsCornerDetailed(defenderStart, key1Feed, key2Feed, params).trajectory;
}

/**
 * Resolve the Cover 4 Palms corner read and return the full result.
 *
 * The read, entirely in observable terms (the corner is given only #1's and #2's positions over
 * time, never route names):
 *  - If #2 releases on an EARLY short out (a shallow out-break in the first few yards — the "Out"
 *    call), the corner jumps off #1 and MANS #2 to the flat.
 *  - Otherwise he plays a MOD man-match on #1 (carry a vertical, bail to the deep quarter on a
 *    short break) — {@link resolveManMatchDetailed}.
 *
 * The "Under"/"Smash" calls (on a #1 shallow/hitch) collapse into the MOD zone drop of the reused
 * man-match read, so they are not modeled as separate branches.
 */
export function resolvePalmsCornerDetailed(
  defenderStart: Position,
  key1Feed: Observation[],
  key2Feed: Observation[],
  params: PalmsCornerParams = DEFAULT_PALMS_CORNER_PARAMS_CB1
): ManMatchResult {
  // Thin adapter over the declarative spec (src/rules/specs/palmsSpecs.ts). His primary key is
  // #1; #2 is a partner feed he watches for the "Out" call, which he now detects the tick it
  // happens rather than by pre-scanning the route.
  return executeResponsibility(
    PALMS_CORNER_SPEC,
    defenderStart,
    key1Feed,
    params,
    params.kinematics ?? DEFAULT_KINEMATICS,
    [key2Feed]
  );
}

/**
 * Cover 4 Palms robber-safety params — FS (strong/left side, keys WR2 as #2, WR1 as #1).
 * Reuses the {@link PalmsCornerParams} shape: `manMatch` is the robber read on #2 (the FS man-match
 * params), and the `out2*` fields detect #2's early short out — the corner's "Out" call — after
 * which the safety mans #1 instead. `maxSpeed`/`trailCushion` tune carrying #1 over the top.
 */
export const DEFAULT_PALMS_SAFETY_PARAMS_FS: PalmsCornerParams = {
  manMatch: DEFAULT_MAN_MATCH_PARAMS_FS, // robber read on #2 (man vertical, else deep quarter)
  out2SignX: -1,     // WR2 (left #2) out breaks toward the left sideline
  out2DepthY: 68,
  out2LateralEps: 12,
  breakAngleDeg: 40,
  maxSpeed: 33,      // carry #1 over the top when the "Out" call fires
  trailCushion: 3,
};

/** Cover 4 Palms robber-safety params — SS (right side, keys WR3 as #2, WR4 as #1). Mirror of FS. */
export const DEFAULT_PALMS_SAFETY_PARAMS_SS: PalmsCornerParams = {
  ...DEFAULT_PALMS_SAFETY_PARAMS_FS,
  manMatch: DEFAULT_MAN_MATCH_PARAMS_SS,
  out2SignX: 1,      // WR3 (right #2) out breaks toward the right sideline
};

/**
 * Resolve the Cover 4 Palms robber safety ("Robber-Read") trajectory. Thin wrapper over
 * {@link resolvePalmsSafetyDetailed} returning only the trajectory.
 */
export function resolvePalmsSafety(
  defenderStart: Position,
  key2Feed: Observation[],
  key1Feed: Observation[],
  params: PalmsCornerParams = DEFAULT_PALMS_SAFETY_PARAMS_FS
): Position[] {
  return resolvePalmsSafetyDetailed(defenderStart, key2Feed, key1Feed, params).trajectory;
}

/**
 * Resolve the Cover 4 Palms robber safety read and return the full result.
 *
 * The read, entirely in observable terms (the safety sees only #2's and #1's positions over time):
 *  - If #2 releases on an EARLY short out (the corner's "Out" call), the corner takes #2, so the
 *    safety MANS #1 — carrying him over the top (mirror laterally, never lurching shallow).
 *  - Otherwise he plays the robber read on #2 ({@link resolveManMatchDetailed} with the FS/SS
 *    params): carry #2 man-to-man if he goes vertical, else drop to the deep quarter (robbing #1).
 */
export function resolvePalmsSafetyDetailed(
  defenderStart: Position,
  key2Feed: Observation[],
  key1Feed: Observation[],
  params: PalmsCornerParams = DEFAULT_PALMS_SAFETY_PARAMS_FS
): ManMatchResult {
  // Thin adapter over the declarative spec (src/rules/specs/palmsSpecs.ts). His primary key is
  // #2 (the robber read); #1 is the partner he takes over the top when the "Out" call fires.
  return executeResponsibility(
    PALMS_SAFETY_SPEC,
    defenderStart,
    key2Feed,
    params,
    params.kinematics ?? DEFAULT_KINEMATICS,
    [key1Feed]
  );
}
